import React, { useState, useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import {
  Trash2,
  Calendar,
  DollarSign,
  AlertCircle,
  Loader2,
  CreditCard,
} from "lucide-react";
import API from "../../utils/api";
import useModal from "hooks/use-modal";

const TableScheduledPayments = ({
  data,
  afterDelete,
  isLoading,
  accountId,
}) => {
  const [updating, setUpdating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const { openModal, closeModal, modalTypes } = useModal();

  // Group payments by plan_id
  const groupedPayments = useMemo(() => {
    return data.reduce((groups, payment) => {
      const planId = payment.plan_id || payment.id;
      if (!groups[planId]) {
        groups[planId] = [];
      }
      groups[planId].push(payment);
      return groups;
    }, {});
  }, [data]);

  // Format date in UTC to avoid timezone shifting
  const formatDateUTC = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  };

  function formatMoney(number, decPlaces, decSep, thouSep) {
    decPlaces = isNaN((decPlaces = Math.abs(decPlaces))) ? 2 : decPlaces;
    decSep = typeof decSep === "undefined" ? "." : decSep;
    thouSep = typeof thouSep === "undefined" ? "," : thouSep;
    var sign = number < 0 ? "-" : "";
    var i = String(
      parseInt((number = Math.abs(Number(number) || 0).toFixed(decPlaces)))
    );
    var j = (j = i.length) > 3 ? j % 3 : 0;

    return (
      sign +
      (j ? i.substr(0, j) + thouSep : "") +
      i.substr(j).replace(/(\decSep{3})(?=\decSep)/g, "$1" + thouSep) +
      (decPlaces
        ? decSep +
          Math.abs(number - i)
            .toFixed(decPlaces)
            .slice(2)
        : "")
    );
  }

  const removePayment = async (id, scheduled, methodDesc, amount, payments = null) => {
    if (updating) return;
    const isPaymentPlan = payments && payments.length > 1;

    openModal(modalTypes.ModalConfirmCancelScheduled, {
      scheduledDate: formatDateUTC(scheduled),
      methodDesc,
      amount,
      payments: isPaymentPlan ? payments : null,
      onCancel: () => closeModal(),
      onConfirm: async () => {
        try {
          setUpdating(true);
          setDeletingId(id);
          if (!accountId) throw new Error("Missing accountId for cancellation");
          await API.delete(`/accounts/${accountId}/payments/${id}`);
          await afterDelete();
        } catch (e) {
          console.log(e);
          // Surface the server's reason (e.g. "Payment cannot be cancelled in
          // its current status") instead of a generic retry message.
          const serverMessage =
            e?.response?.data?.error ||
            e?.response?.data?.message ||
            (isPaymentPlan
              ? "Failed to cancel payment plan. Please try again."
              : "Failed to cancel payment. Please try again.");
          alert(serverMessage);
        } finally {
          setUpdating(false);
          setDeletingId(null);
        }
      },
    });
  };

  const getDaysUntil = (date) => {
    const today = new Date();
    const paymentDate = new Date(date);
    const diffTime = paymentDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Tomorrow";
    if (diffDays < 0) return "Overdue";
    return `In ${diffDays} days`;
  };

  return (
    <div
      className={`w-full ${updating ? "opacity-60 pointer-events-none" : ""}`}
    >
      <table className="w-full hidden sm:table">
        <thead>
          <tr className="border-b border-gray-600">
            <th className="text-left py-3 px-4 font-semibold text-gray-300 text-sm">
              Next Payment
            </th>
            <th className="text-left py-3 px-4 font-semibold text-gray-300 text-sm">
              Method
            </th>
            <th className="text-right py-3 px-4 font-semibold text-gray-300 text-sm">
              Amount
            </th>
            <th className="text-right py-3 px-4 font-semibold text-gray-300 text-sm">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading &&
            Array.from({ length: 3 }).map((_, index) => (
              <tr
                key={`skel-${index}`}
                className={`border-b border-gray-700 ${index % 2 === 0 ? "" : "bg-gray-800/30"}`}
              >
                <td className="py-3 px-4">
                  <Skeleton
                    height={16}
                    width={160}
                    baseColor="#374151"
                    highlightColor="#4B5563"
                  />
                  <div className="mt-1">
                    <Skeleton
                      height={12}
                      width={80}
                      baseColor="#374151"
                      highlightColor="#4B5563"
                    />
                  </div>
                </td>
                <td className="py-3 px-4 text-right">
                  <Skeleton
                    height={18}
                    width={80}
                    baseColor="#374151"
                    highlightColor="#4B5563"
                  />
                </td>
              </tr>
            ))}
          {!isLoading &&
            Object.entries(groupedPayments).map(([planId, planPayments], groupIndex) => {
              const isPaymentPlan = planPayments.length > 1 && planPayments[0].plan_id;
              const planTotal = planPayments.reduce((sum, p) => sum + p.amount, 0);
              const firstPayment = planPayments[0];

              if (isPaymentPlan) {
                // Render payment plan group
                return (
                  <React.Fragment key={planId}>
                    {/* Plan header row */}
                    <tr className="border-b border-blue-600/30 bg-blue-900/10">
                      <td colSpan={3} className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <CreditCard size={16} className="text-blue-400" />
                          <span className="text-blue-300 font-semibold text-sm">
                            Payment Plan ({planPayments.length} payments)
                          </span>
                          <span className="text-gray-400 text-sm ml-2">
                            Total: ${formatMoney(planTotal)}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() =>
                            removePayment(
                              firstPayment.id,
                              firstPayment.scheduled || firstPayment.date,
                              firstPayment.method_desc ||
                                firstPayment.method?.method_desc ||
                                firstPayment.method?.label,
                              planTotal,
                              planPayments
                            )
                          }
                          disabled={updating}
                          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Cancel payment plan"
                        >
                          {deletingId === firstPayment.id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Trash2 size={14} />
                          )}
                          Cancel Plan
                        </button>
                      </td>
                    </tr>
                    {/* Individual payments in plan */}
                    {planPayments.map((row, index) => (
                      <tr
                        key={row.id}
                        className={`border-b border-gray-700/50 bg-blue-900/5 ${
                          index === planPayments.length - 1 ? "border-b-blue-600/30" : ""
                        }`}
                      >
                        <td className="py-2 px-4 pl-8">
                          <div className="flex items-start gap-2">
                            <Calendar size={14} className="text-gray-500 mt-0.5" />
                            <div>
                              <div className="text-gray-300 text-sm">
                                {formatDateUTC(row.scheduled || row.date)}
                              </div>
                              <div
                                className={`text-xs font-medium ${
                                  getDaysUntil(row.scheduled || row.date) ===
                                  "Overdue"
                                    ? "text-red-400"
                                    : getDaysUntil(row.scheduled || row.date) ===
                                        "Today"
                                      ? "text-orange-400"
                                      : getDaysUntil(row.scheduled || row.date) ===
                                          "Tomorrow"
                                        ? "text-yellow-400"
                                        : "text-gray-500"
                                }`}
                              >
                                {getDaysUntil(row.scheduled || row.date)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-2 px-4">
                          <span className="text-gray-400 text-sm">
                            {row.method_desc ||
                              row.method?.method_desc ||
                              row.method?.label ||
                              "—"}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <DollarSign size={14} className="text-gray-600" />
                            <span className="text-base font-medium text-gray-300">
                              {formatMoney(row.amount)}
                            </span>
                          </div>
                        </td>
                        <td className="py-2 px-4"></td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              }

              // Render individual payment (not part of a plan)
              const row = firstPayment;
              return (
                <tr
                  key={row.id}
                  className={`border-b border-gray-700 hover:bg-gray-700/50 transition-colors ${
                    groupIndex % 2 === 0 ? "" : "bg-gray-800/30"
                  }`}
                >
                  <td className="py-3 px-4">
                    <div className="flex items-start gap-2">
                      <Calendar size={16} className="text-gray-400 mt-0.5" />
                      <div>
                        <div className="text-gray-200 text-sm font-medium">
                          {formatDateUTC(row.scheduled || row.date)}
                        </div>
                        <div
                          className={`text-xs font-medium ${
                            getDaysUntil(row.scheduled || row.date) ===
                            "Overdue"
                              ? "text-red-400"
                              : getDaysUntil(row.scheduled || row.date) ===
                                  "Today"
                                ? "text-orange-400"
                                : getDaysUntil(row.scheduled || row.date) ===
                                    "Tomorrow"
                                  ? "text-yellow-400"
                                  : "text-gray-400"
                          }`}
                        >
                          {getDaysUntil(row.scheduled || row.date)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-gray-300 text-sm">
                      {row.method_desc ||
                        row.method?.method_desc ||
                        row.method?.label ||
                        "—"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <DollarSign size={16} className="text-gray-500" />
                      <span className="text-lg font-semibold text-gray-200">
                        {formatMoney(row.amount)}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={() =>
                        removePayment(
                          row.id,
                          row.scheduled || row.date,
                          row.method_desc ||
                            row.method?.method_desc ||
                            row.method?.label,
                          row.amount
                        )
                      }
                      disabled={updating}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Cancel scheduled payment"
                    >
                      {deletingId === row.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Trash2 size={14} />
                      )}
                      Cancel
                    </button>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>

      {/* Mobile stacked layout */}
      <div className="sm:hidden space-y-3">
        {isLoading &&
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={`skel-m-${i}`}
              className="p-3 rounded-lg border border-gray-700 bg-gray-800/50"
            >
              <div className="flex justify-between text-sm mb-2">
                <Skeleton
                  height={14}
                  width={140}
                  baseColor="#374151"
                  highlightColor="#4B5563"
                />
                <Skeleton
                  height={14}
                  width={70}
                  baseColor="#374151"
                  highlightColor="#4B5563"
                />
              </div>
              <Skeleton
                height={14}
                width={120}
                baseColor="#374151"
                highlightColor="#4B5563"
              />
            </div>
          ))}
        {!isLoading &&
          Object.entries(groupedPayments).map(([planId, planPayments]) => {
            const isPaymentPlan = planPayments.length > 1 && planPayments[0].plan_id;
            const planTotal = planPayments.reduce((sum, p) => sum + p.amount, 0);
            const firstPayment = planPayments[0];

            if (isPaymentPlan) {
              // Render payment plan group (mobile)
              return (
                <div
                  key={planId}
                  className="rounded-lg border border-blue-600/30 bg-blue-900/10 overflow-hidden"
                >
                  {/* Plan header */}
                  <div className="p-3 border-b border-blue-600/20 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CreditCard size={16} className="text-blue-400" />
                      <span className="text-blue-300 font-semibold text-sm">
                        Payment Plan ({planPayments.length} payments)
                      </span>
                    </div>
                    <button
                      onClick={() =>
                        removePayment(
                          firstPayment.id,
                          firstPayment.scheduled || firstPayment.date,
                          firstPayment.method_desc ||
                            firstPayment.method?.method_desc ||
                            firstPayment.method?.label,
                          planTotal,
                          planPayments
                        )
                      }
                      disabled={updating}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors disabled:opacity-50"
                      title="Cancel payment plan"
                    >
                      {deletingId === firstPayment.id ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Trash2 size={12} />
                      )}
                      Cancel
                    </button>
                  </div>
                  {/* Individual payments */}
                  <div className="divide-y divide-gray-700/50">
                    {planPayments.map((row) => (
                      <div key={row.id} className="p-3 bg-blue-900/5">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="text-gray-300 text-sm">
                              {formatDateUTC(row.scheduled || row.date)}
                            </div>
                            <div className="text-gray-500 text-xs mt-0.5">
                              {row.method_desc ||
                                row.method?.method_desc ||
                                row.method?.label ||
                                "—"}
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-medium text-gray-300">
                              ${formatMoney(row.amount)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Plan total */}
                  <div className="p-3 border-t border-blue-600/20 flex justify-between items-center bg-blue-900/10">
                    <span className="text-gray-400 text-sm">Total</span>
                    <span className="text-base font-semibold text-gray-200">
                      ${formatMoney(planTotal)}
                    </span>
                  </div>
                </div>
              );
            }

            // Render individual payment (mobile)
            const row = firstPayment;
            return (
              <div
                key={row.id}
                className="p-3 rounded-lg border border-gray-700 bg-gray-800/50"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-gray-200 text-sm font-medium">
                      {formatDateUTC(row.scheduled || row.date)}
                    </div>
                    <div className="text-gray-300 text-sm mt-1">
                      {row.method_desc ||
                        row.method?.method_desc ||
                        row.method?.label ||
                        "—"}
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    <span className="text-base font-semibold text-gray-200">
                      ${formatMoney(row.amount)}
                    </span>
                    <button
                      onClick={() =>
                        removePayment(
                          row.id,
                          row.scheduled || row.date,
                          row.method_desc ||
                            row.method?.method_desc ||
                            row.method?.label,
                          row.amount
                        )
                      }
                      disabled={updating}
                      className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors disabled:opacity-50"
                      title="Cancel scheduled payment"
                    >
                      {deletingId === row.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Trash2 size={14} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {data.length === 0 && (
        <div className="py-8 text-center">
          <AlertCircle size={24} className="text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500">No scheduled payments found</p>
        </div>
      )}
    </div>
  );
};

export default TableScheduledPayments;
